import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type { CardiacWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("cardiac");

const VIEW_OPTIONS = [
  { key: "plax", label: "PLAX" },
  { key: "psax", label: "PSAX" },
  { key: "apical4Chamber", label: "A4C" },
  { key: "subxiphoid", label: "Subxiphoid" },
] as const;

const LV_FUNCTION_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "reduced", label: "Reduced" },
  { value: "severely_reduced", label: "Severely Reduced" },
] as const;

const EFFUSION_OPTIONS = [
  { value: "none", label: "None" },
  { value: "small", label: "Small" },
  { value: "moderate", label: "Moderate" },
  { value: "large", label: "Large" },
] as const;

export const isCardiacComplete = (data: CardiacWorksheetData): boolean =>
  data.findings.lvFunction !== null &&
  data.findings.rvDilatation !== null &&
  data.findings.pericardialEffusion !== null &&
  data.findings.ivcCollapsible !== null;

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

export const CardiacWorksheet: FC<{
  data: CardiacWorksheetData;
  onChange: (data: CardiacWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateFindings = (patch: Partial<CardiacWorksheetData["findings"]>) => {
    onChange({ ...data, findings: { ...data.findings, ...patch } });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as CardiacWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <WorksheetSection label="Findings">
            <FindingLabel>LV Function</FindingLabel>
            <EnumChipSelect
              options={[...LV_FUNCTION_OPTIONS]}
              selected={data.findings.lvFunction}
              onChange={(v) =>
                updateFindings({
                  lvFunction:
                    v as CardiacWorksheetData["findings"]["lvFunction"],
                })
              }
              disabled={readOnly}
            />

            <FindingLabel>RV Dilation</FindingLabel>
            <BooleanToggle
              value={data.findings.rvDilatation}
              onChange={(v) => updateFindings({ rvDilatation: v })}
              disabled={readOnly}
            />

            <FindingLabel>Pericardial Effusion</FindingLabel>
            <EnumChipSelect
              options={[...EFFUSION_OPTIONS]}
              selected={data.findings.pericardialEffusion}
              onChange={(v) =>
                updateFindings({
                  pericardialEffusion:
                    v as CardiacWorksheetData["findings"]["pericardialEffusion"],
                })
              }
              disabled={readOnly}
            />

            <FindingLabel>IVC</FindingLabel>
            <EnumChipSelect
              options={[
                { value: "collapsible", label: "Collapsible" },
                { value: "non-collapsible", label: "Non-collapsible" },
              ]}
              selected={
                data.findings.ivcCollapsible === true
                  ? "collapsible"
                  : data.findings.ivcCollapsible === false
                    ? "non-collapsible"
                    : null
              }
              onChange={(v) =>
                updateFindings({
                  ivcCollapsible:
                    v === "collapsible"
                      ? true
                      : v === "non-collapsible"
                        ? false
                        : null,
                })
              }
              disabled={readOnly}
            />

            <FindingLabel>Spectral Doppler captured</FindingLabel>
            <BooleanToggle
              value={data.findings.spectralDoppler}
              onChange={(v) => updateFindings({ spectralDoppler: v })}
              disabled={readOnly}
            />

            <FindingLabel>Color Flow Doppler captured</FindingLabel>
            <BooleanToggle
              value={data.findings.colorFlowDoppler}
              onChange={(v) => updateFindings({ colorFlowDoppler: v })}
              disabled={readOnly}
            />
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
